// /inventory/products/:id · per-product detail page (Phase 2b).
// Current balances, recent events, and a 14-day made/sold/wasted sparkline.

import { useEffect, useState } from 'react';
import * as api from '../lib/api.ts';
import { navigate } from '../lib/routing.js';
import { LOCATIONS as LOCS, locationLabel } from '../lib/locations.ts';

const KIND_LABEL = {
  produced: '生产', received: '收货', sold: '售出',
  wasted: '报废', transferred: '调拨', adjusted: '调整'
};

export const InventoryProductDetail = ({ productId }) => {
  const [product, setProduct] = useState(null);
  const [balances, setBalances] = useState(null);
  const [events, setEvents] = useState([]);
  const [trend, setTrend] = useState({ rows: [], days: 14 });
  const [thresholds, setThresholds] = useState([]);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.listProducts({ includeInactive: true }),                  // include inactive
      api.loadInventoryGrid(),
      api.listInventoryEvents({ product_id: productId, limit: 25 }),
      api.loadProductTrend(productId, 14),
      api.listThresholds().catch(() => [])
    ])
      .then(([allProducts, grid, evs, tr, ts]) => {
        if (cancelled) return;
        const p = allProducts.find(x => x.id === productId);
        if (!p) { setErr('产品不存在'); return; }
        setProduct(p);
        const row = grid.find(g => g.id === productId);
        setBalances(row?.balances ?? Object.fromEntries(LOCS.map(l => [l, 0])));
        setEvents(evs);
        setTrend(tr);
        setThresholds(ts);
      })
      .catch(e => { if (!cancelled) setErr(e?.message || '加载失败'); });
    return () => { cancelled = true; };
  }, [productId]);

  if (err) return (
    <>
      <div className="vava-page-header"><h1 className="vava-page-title">产品详情</h1></div>
      <div className="att-error" role="alert">{err}</div>
      <button className="btn btn-secondary" style={{ marginTop: 12 }}
              onClick={() => navigate('/inventory/products')}>← 返回产品列表</button>
    </>
  );
  if (!product) return <div className="vava-card empty-state">加载中…</div>;

  const thresholdMap = new Map(thresholds.map(t => [`${t.productId}|${t.location}`, t.threshold]));

  return (
    <>
      <div className="vava-page-header">
        <div>
          <h1 className="vava-page-title">
            {product.name}
            {!product.active && (
              <span className="status-badge user-state-deactivated"
                    style={{ marginLeft: 12, fontWeight: 500, fontSize: 13 }}>已停用</span>
            )}
          </h1>
          <div className="vava-page-meta">
            <span className="mono">{product.id}</span>
            {product.category && <> · {product.category}</>}
          </div>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate('/inventory')}>← 返回 当日现货</button>
      </div>

      {/* Current balances */}
      <div className="vava-card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px' }}>当前库存</h2>
        <div className="inv-pills">
          {LOCS.map(loc => {
            const qty = balances[loc];
            const threshold = thresholdMap.get(`${product.id}|${loc}`);
            const low = threshold !== undefined && qty < threshold;
            const zero = qty === 0;
            return (
              <div key={loc} className={`inv-pill ${low ? 'low' : zero ? 'zero' : ''}`}
                   title={threshold !== undefined ? `阈值 ${threshold}` : undefined}>
                <span className="loc">{locationLabel(loc)}</span>
                <span className="qty">{qty}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* 14-day trend sparkline */}
      <div className="vava-card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px' }}>14 天趋势</h2>
        <TrendChart trend={trend} />
      </div>

      {/* Recent events */}
      <div className="vava-card" style={{ padding: 0 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0, padding: '14px 24px 0' }}>近期记录</h2>
        {events.length === 0 ? (
          <div className="empty-state" style={{ padding: 24 }}>暂无记录</div>
        ) : (
          <table className="vava-table">
            <thead>
              <tr>
                <th>时间</th>
                <th>类型</th>
                <th>地点</th>
                <th style={{ textAlign: 'right' }}>数量</th>
                <th>原因 / 备注</th>
              </tr>
            </thead>
            <tbody>
              {events.map(e => (
                <tr key={e.id}>
                  <td className="mono" style={{ fontSize: 12 }}>{e.createdAt}</td>
                  <td>{KIND_LABEL[e.kind] || e.kind}</td>
                  <td className="mono">{e.location}</td>
                  <td className="mono" style={{ textAlign: 'right',
                                                color: e.qty < 0 ? 'var(--vava-red)' : 'var(--vava-green)' }}>
                    {e.qty > 0 ? `+${e.qty}` : e.qty}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--vava-text-muted)' }}>
                    {e.reason || ''}{e.reason && e.note && ' · '}{e.note || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

// ---------- TrendChart ------------------------------------------------------

const KIND_COLORS = {
  produced: 'var(--vava-green)',
  sold: 'var(--vava-tan-hover)',
  wasted: 'var(--vava-red)'
};
const KIND_LEGEND = {
  produced: '生产', sold: '售出', wasted: '报废'
};

function dateRange(days) {
  const today = new Date();
  const out = [];
  for (let i = days - 1; i >= 0; i--) {
    out.push(new Date(today.getTime() - i * 86_400_000).toISOString().slice(0, 10));
  }
  return out;
}

const TrendChart = ({ trend }) => {
  const dates = dateRange(trend.days);
  const series = { produced: [], sold: [], wasted: [] };
  for (const kind of Object.keys(series)) {
    series[kind] = dates.map(d => {
      const row = trend.rows.find(r => r.day === d && r.kind === kind);
      return row?.qty ?? 0;
    });
  }
  const max = Math.max(1, ...Object.values(series).flat());
  const W = 600, H = 120, P = 8;        // padding
  const chartW = W - P * 2, chartH = H - P * 2;
  const xStep = dates.length > 1 ? chartW / (dates.length - 1) : 0;
  const y = (v) => P + chartH - (v / max) * chartH;

  function path(arr) {
    return arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${P + i * xStep} ${y(v)}`).join(' ');
  }

  const allZero = Object.values(series).every(arr => arr.every(v => v === 0));
  if (allZero) {
    return <div style={{ color: 'var(--vava-text-muted)', fontSize: 13, padding: '8px 0' }}>
      最近 {trend.days} 天没有 生产 / 售出 / 报废 记录。
    </div>;
  }

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H + 24}`} preserveAspectRatio="none"
           style={{ display: 'block', width: '100%', height: 'auto', maxHeight: 200 }}>
        {/* Baseline (y = 0) */}
        <line x1={P} y1={H - P} x2={W - P} y2={H - P}
              stroke="var(--vava-border)" strokeWidth="1" />
        {/* Series polylines */}
        {Object.entries(series).map(([kind, arr]) => (
          <g key={kind}>
            <path d={path(arr)} stroke={KIND_COLORS[kind]} fill="none" strokeWidth="2" />
            {arr.map((v, i) => v > 0 && (
              <circle key={i} cx={P + i * xStep} cy={y(v)} r="3" fill={KIND_COLORS[kind]} />
            ))}
          </g>
        ))}
        {/* Y-axis tick */}
        <text x={P} y={P + 12} fontSize="11" fill="var(--vava-text-muted)">最高 {max}</text>
        {/* X-axis: first + last date */}
        <text x={P} y={H + 16} fontSize="11" fill="var(--vava-text-muted)">{dates[0]}</text>
        <text x={W - P} y={H + 16} fontSize="11" fill="var(--vava-text-muted)" textAnchor="end">{dates[dates.length - 1]}</text>
      </svg>
      <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12, color: 'var(--vava-text-muted)' }}>
        {Object.entries(KIND_LEGEND).map(([k, label]) => {
          const total = series[k].reduce((a, b) => a + b, 0);
          return (
            <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 10, height: 10, borderRadius: 5, background: KIND_COLORS[k] }} />
              {label} <span className="mono">{total}</span>
            </span>
          );
        })}
      </div>
    </div>
  );
};
